import { useState } from 'react'
import { Braces, Loader2 } from 'lucide-react'
import { getIdToken } from '@/services/auth'
import { isFrontFlowOrg } from '@/constants/frontflowOrg'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from '@/hooks/use-toast'

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || ''

/**
 * Opens the extraction a customer order was built from, in a tab of its own.
 *
 * Ours to read, not a customer's: the endpoint behind it refuses anyone but
 * the root organization, and this only draws the link for us so the rest of
 * the page does not advertise a door that will not open.
 *
 * The tab is opened before the request rather than after it. A window.open
 * that follows an await has lost its claim to have come from the click and is
 * what every popup blocker stops.
 */
export function ExtractionLink({ customerOrderId }: { customerOrderId: string }) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)

  if (!isFrontFlowOrg(user?.tenantId)) return null

  async function open() {
    const tab = window.open('', '_blank')
    if (!tab) {
      toast({ variant: 'destructive', description: 'Allow pop-ups to open the extraction.' })
      return
    }

    tab.document.write('Loading the extraction…')
    setLoading(true)
    try {
      const token = await getIdToken()
      const response = await fetch(
        `${API_BASE_URL}/v1/admin/customer-orders/${customerOrderId}/extraction`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )

      if (!response.ok) {
        tab.close()
        const message = response.status === 404
          ? 'There is no stored extraction for this order.'
          : 'Could not load the extraction.'
        toast({ variant: 'destructive', description: message })
        return
      }

      // Rendered from a blob rather than by pointing the tab at the endpoint:
      // the request needs a bearer token, and a tab navigating on its own
      // cannot carry one.
      const text = await response.text()
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }))
      tab.location.href = url
    } catch {
      tab.close()
      toast({ variant: 'destructive', description: 'Could not load the extraction.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 disabled:opacity-60"
      title="Open the stored extraction.json in a new tab"
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Braces className="h-3 w-3" />}
      View extraction
    </button>
  )
}
